import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withRouter, Switch as RouterSwitch } from 'react-router-dom'
import {
  Button,
  Modal,
  Table,
  Tag,
  Input,
  InputNumber,
  Select,
  Switch,
  Space,
  Empty,
  Popconfirm,
  Row,
  Col
} from 'antd'
import PageLoader from '../../../components/Layouts/PageLoader'
import PrivateRoute from '../../../hocs/PrivateRoute'
import PaginationContainer from '../../../components/Forms/Pagination'
import {
  getRequest,
  postRequest,
  patchRequest,
  deleteRequest,
  formatUrl,
  dutyMsrpWhitelistUrl,
  carMakesUrl,
  carModelsUrl,
  originsUrl,
  fuelTypesUrl,
  driveTrainsUrl,
  successNotification,
  errorNotification
} from '../../../config'

const { Option } = Select

// The MSRP currencies the duty pipeline can convert to GHS (matches the w-api CURRENCY_LIST /
// market currencies). GHS is excluded — an MSRP is always in a foreign market currency.
const CURRENCIES = ['USD', 'AED', 'CNY', 'JPY', 'KRW', 'EUR', 'CAD']

const CURRENT_YEAR = new Date().getFullYear()

const BLANK = {
  make: undefined,
  model: undefined,
  origin: undefined,
  carYear: CURRENT_YEAR,
  appliesToAllTrims: true,
  trim: '',
  fuelType: undefined,
  driveTrain: undefined,
  engineSize: null,
  msrpAmount: null,
  msrpCurrency: 'USD',
  isActive: true,
  notes: ''
}

const MsrpWhitelistList = () => {
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [meta, setMeta] = useState({ previous: null, next: null, count: 0 })
  const [search, setSearch] = useState('')
  const [sourceFilter, setSourceFilter] = useState(undefined)
  const [reviewOnly, setReviewOnly] = useState(false)
  const [editing, setEditing] = useState(null) // record being edited, or {} for new
  const [form, setForm] = useState(BLANK)
  const [saving, setSaving] = useState(false)

  // Reference options for the FK selects.
  const [makes, setMakes] = useState([])
  const [models, setModels] = useState([])
  const [origins, setOrigins] = useState([])
  const [fuelTypes, setFuelTypes] = useState([])
  const [driveTrains, setDriveTrains] = useState([])

  // Filters are passed in explicitly rather than read from state: a click handler that also
  // sets state would otherwise fetch with the previous value.
  const buildUrl = (filters = {}) => {
    const { source = sourceFilter, review = reviewOnly, term = search } = filters
    const params = new URLSearchParams()
    if (term) params.append('search', term)
    if (source) params.append('source', source)
    if (review) params.append('needs_review', 'true')
    const qs = params.toString()
    return qs ? `${dutyMsrpWhitelistUrl}?${qs}` : dutyMsrpWhitelistUrl
  }

  const getRows = async (url = null, filters = {}) => {
    const response = await getRequest(
      url ? formatUrl(url) : buildUrl(filters),
      'whitelist',
      setLoading
    )
    if (response.success) {
      setRows(response.data.results || response.data)
      setMeta({ ...response.data })
    }
  }

  useEffect(() => {
    getRows()
    const loadRefs = async () => {
      const mk = await getRequest(`${carMakesUrl}/all`, 'makes', () => {})
      if (mk.success) setMakes((mk.data || []).map(m => ({ id: m.id, name: m.name })))
      const og = await getRequest(`${originsUrl}/all`, 'origins', () => {})
      if (og.success) {
        setOrigins(
          (og.data || []).map(o => ({
            id: o.id,
            name: o.province_state ? `${o.province_state}, ${o.country}` : o.country
          }))
        )
      }
      const ft = await getRequest(`${fuelTypesUrl}/all`, 'fuel types', () => {})
      if (ft.success) setFuelTypes((ft.data || []).map(f => ({ id: f.id, name: f.name })))
      const dt = await getRequest(`${driveTrainsUrl}/all`, 'drive trains', () => {})
      if (dt.success) setDriveTrains((dt.data || []).map(d => ({ id: d.id, name: d.name })))
    }
    loadRefs()
    // eslint-disable-next-line
  }, [])

  // Load the models for the make chosen in the form.
  const loadModels = async makeId => {
    if (!makeId) {
      setModels([])
      return
    }
    const res = await postRequest(
      `${carModelsUrl}/for-make`,
      { make: makeId },
      'models',
      () => {}
    )
    if (res.success) setModels((res.data || []).map(m => ({ id: m.id, name: m.name })))
  }

  const setField = key => value => setForm(f => ({ ...f, [key]: value }))

  const onMakeChange = value => {
    setForm(f => ({ ...f, make: value, model: undefined }))
    loadModels(value)
  }

  const openNew = () => {
    setForm(BLANK)
    setModels([])
    setEditing({})
  }

  const openEdit = record => {
    setForm({
      make: record.make,
      model: record.model,
      origin: record.origin,
      carYear: record.carYear || CURRENT_YEAR,
      appliesToAllTrims: record.appliesToAllTrims,
      trim: record.trim || '',
      fuelType: record.fuelType || undefined,
      driveTrain: record.driveTrain || undefined,
      engineSize: record.engineSize ? Number(record.engineSize) : null,
      msrpAmount: record.msrpAmount ? Number(record.msrpAmount) : null,
      msrpCurrency: record.msrpCurrency || 'USD',
      isActive: record.isActive,
      notes: record.notes || ''
    })
    loadModels(record.make)
    setEditing(record)
  }

  const handleSave = async () => {
    if (!form.make || !form.model || !form.origin) {
      errorNotification('Make, model and origin are required')
      return
    }
    if (!form.carYear) {
      errorNotification('Year is required')
      return
    }
    if (form.msrpAmount === null || form.msrpAmount === undefined || Number(form.msrpAmount) <= 0) {
      errorNotification('Enter a valid MSRP amount')
      return
    }
    if (!form.appliesToAllTrims && !form.trim.trim()) {
      errorNotification('Enter a trim, or turn on "applies to all trims"')
      return
    }
    const payload = {
      ...form,
      trim: form.appliesToAllTrims ? '' : form.trim,
      // Send explicit null for blank discriminators so clearing them on edit actually persists.
      fuelType: form.fuelType || null,
      driveTrain: form.driveTrain || null,
      engineSize:
        form.engineSize === '' || form.engineSize === undefined ? null : form.engineSize
    }
    const isNew = !editing.id
    const res = isNew
      ? await postRequest(dutyMsrpWhitelistUrl + '/', payload, 'entry', setSaving)
      : await patchRequest(
          `${dutyMsrpWhitelistUrl}/${editing.id}/`,
          payload,
          'entry',
          setSaving
        )
    if (res.success) {
      successNotification(isNew ? 'Whitelist entry added' : 'Whitelist entry updated')
      setEditing(null)
      getRows()
    }
  }

  const handleDelete = async record => {
    const res = await deleteRequest(
      `${dutyMsrpWhitelistUrl}/${record.id}/`,
      'entry',
      setLoading
    )
    if (res.success) {
      successNotification('Whitelist entry removed')
      getRows()
    }
  }

  /* eslint-disable react/display-name */
  const columns = [
    { title: 'Year', dataIndex: 'carYear', key: 'carYear', width: 70 },
    {
      title: 'Car',
      key: 'car',
      render: (_, r) => (
        <span>
          <b>{r.makeName}</b> {r.modelName}
          {!r.isActive && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              Inactive
            </Tag>
          )}
        </span>
      )
    },
    { title: 'Origin', dataIndex: 'originLabel', key: 'originLabel' },
    {
      title: 'Source',
      key: 'source',
      width: 130,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Tag color={r.source === 'SYSTEM' ? 'default' : 'blue'}>
            {r.source === 'SYSTEM' ? 'System' : 'Admin'}
          </Tag>
          {r.needsReview ? <Tag color="red">Check value</Tag> : null}
        </Space>
      )
    },
    {
      title: 'Trim',
      key: 'trim',
      render: (_, r) =>
        r.appliesToAllTrims ? <Tag color="blue">All trims</Tag> : r.trim || '—'
    },
    {
      title: 'Variant',
      key: 'variant',
      render: (_, r) => {
        const parts = [
          r.fuelTypeName,
          r.driveTrainName,
          r.engineSize ? `${Number(r.engineSize)}L` : null
        ].filter(Boolean)
        return parts.length ? (
          <Space size={[0, 4]} wrap>
            {parts.map((p, i) => (
              <Tag key={i}>{p}</Tag>
            ))}
          </Space>
        ) : (
          <span style={{ color: '#aaa' }}>Any</span>
        )
      }
    },
    {
      title: 'Customs MSRP',
      key: 'msrp',
      render: (_, r) =>
        `${r.msrpCurrency} ${Number(r.msrpAmount || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`
    },
    {
      title: 'Action',
      key: 'action',
      width: 170,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title={
              record.source === 'SYSTEM'
                ? 'Remove this system entry?'
                : 'Remove this entry?'
            }
            description={
              record.source === 'SYSTEM' ? (
                <div style={{ maxWidth: 320 }}>
                  The harvester will add this car again on its next run. To reject the value for
                  good, <b>Edit</b> it and switch <b>Active</b> off instead — a deactivated entry
                  is never re-created.
                </div>
              ) : null
            }
            onConfirm={() => handleDelete(record)}
            okText="Remove"
            cancelText="Cancel"
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]
  /* eslint-enable react/display-name */

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <h3 style={{ marginBottom: 8 }}>
            Customs MSRP Whitelist {meta && `(${meta.count || rows.length})`}
          </h3>
          <p style={{ color: '#888', marginBottom: 16 }}>
            Cars whose duty we compute from Ghana customs&apos; own (usually lower) HDV value
            instead of the manufacturer&apos;s MSRP. A matching car uses this value directly and
            skips the AI lookup. An entry can cover all trims of a car/year/origin or one specific
            trim (a specific-trim entry wins).
          </p>
          <p style={{ color: '#888', marginBottom: 16 }}>
            <b>System</b> entries are prices the assistant already looked up and validated, kept so
            the same car is never searched twice — they are not verified customs figures. An{' '}
            <b>Admin</b> entry always wins over a System one for the same car, so correcting a bad
            value is a matter of adding your own.
          </p>
          <Space wrap style={{ marginBottom: 16 }}>
            <Input.Search
              allowClear
              placeholder="Search make / model / trim"
              style={{ width: 260 }}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onSearch={() => getRows()}
            />
            <Select
              allowClear
              placeholder="Source"
              style={{ width: 150 }}
              value={sourceFilter}
              onChange={v => {
                setSourceFilter(v)
                getRows(null, { source: v })
              }}
            >
              <Option value="ADMIN">Admin (curated)</Option>
              <Option value="SYSTEM">System (harvested)</Option>
            </Select>
            <Button
              type={reviewOnly ? 'primary' : 'default'}
              danger={reviewOnly}
              onClick={() => {
                const next = !reviewOnly
                setReviewOnly(next)
                getRows(null, { review: next })
              }}
            >
              Needs review
            </Button>
            <Button type="primary" onClick={openNew}>
              + Add Entry
            </Button>
            <Button onClick={() => getRows()}>Refresh</Button>
          </Space>

          <Table
            rowKey="id"
            dataSource={rows}
            columns={columns}
            pagination={false}
            scroll={{ x: 800 }}
            locale={{ emptyText: <Empty description="No whitelist entries yet" /> }}
          />
          {rows.length ? (
            <PaginationContainer meta={meta} getData={url => getRows(url)} />
          ) : null}
        </div>
      </div>

      <Modal
        title={editing?.id ? 'Edit whitelist entry' : 'Add whitelist entry'}
        centered
        open={!!editing}
        onOk={handleSave}
        onCancel={() => setEditing(null)}
        okText="Save"
        confirmLoading={saving}
        width={640}
      >
        <Row gutter={[12, 12]}>
          <Col xs={24} md={8}>
            <label>Make</label>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Make"
              style={{ width: '100%' }}
              value={form.make}
              onChange={onMakeChange}
            >
              {makes.map(m => (
                <Option key={m.id} value={m.id}>
                  {m.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <label>Model</label>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder={form.make ? 'Model' : 'Pick a make first'}
              disabled={!form.make}
              style={{ width: '100%' }}
              value={form.model}
              onChange={setField('model')}
            >
              {models.map(m => (
                <Option key={m.id} value={m.id}>
                  {m.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <label>Year</label>
            <InputNumber
              min={1960}
              max={CURRENT_YEAR + 1}
              style={{ width: '100%' }}
              value={form.carYear}
              onChange={setField('carYear')}
            />
          </Col>
          <Col xs={24} md={12}>
            <label>Origin</label>
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Origin"
              style={{ width: '100%' }}
              value={form.origin}
              onChange={setField('origin')}
            >
              {origins.map(o => (
                <Option key={o.id} value={o.id}>
                  {o.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} md={6}>
            <label>Customs MSRP</label>
            <InputNumber
              min={0}
              precision={2}
              style={{ width: '100%' }}
              placeholder="Amount"
              value={form.msrpAmount}
              onChange={setField('msrpAmount')}
            />
          </Col>
          <Col xs={12} md={6}>
            <label>Currency</label>
            <Select
              style={{ width: '100%' }}
              value={form.msrpCurrency}
              onChange={setField('msrpCurrency')}
            >
              {CURRENCIES.map(c => (
                <Option key={c} value={c}>
                  {c}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={12}>
            <label style={{ display: 'block' }}>Applies to all trims</label>
            <Switch
              checked={form.appliesToAllTrims}
              onChange={setField('appliesToAllTrims')}
            />
          </Col>
          {!form.appliesToAllTrims && (
            <Col xs={24} md={12}>
              <label>Trim</label>
              <Input
                placeholder="e.g. F, RS, LE"
                value={form.trim}
                onChange={e => setField('trim')(e.target.value)}
              />
            </Col>
          )}
          <Col xs={24}>
            <p style={{ color: '#888', fontSize: 12, margin: '4px 0 0' }}>
              Variant discriminators (optional) — leave blank to apply to any. Set them to give a
              distinct value to, say, the hybrid or the 1.3L version. The most specific matching
              entry is used.
            </p>
          </Col>
          <Col xs={24} md={8}>
            <label>Fuel type</label>
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Any"
              style={{ width: '100%' }}
              value={form.fuelType}
              onChange={setField('fuelType')}
            >
              {fuelTypes.map(f => (
                <Option key={f.id} value={f.id}>
                  {f.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <label>Drive train</label>
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Any"
              style={{ width: '100%' }}
              value={form.driveTrain}
              onChange={setField('driveTrain')}
            >
              {driveTrains.map(d => (
                <Option key={d.id} value={d.id}>
                  {d.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <label>Engine size (L)</label>
            <InputNumber
              min={0}
              step={0.1}
              precision={2}
              placeholder="Any"
              style={{ width: '100%' }}
              value={form.engineSize}
              onChange={setField('engineSize')}
            />
          </Col>
          <Col xs={24} md={12}>
            <label style={{ display: 'block' }}>Active</label>
            <Switch checked={form.isActive} onChange={setField('isActive')} />
          </Col>
          <Col xs={24}>
            <label>Notes</label>
            <Input.TextArea
              rows={2}
              value={form.notes}
              onChange={e => setField('notes')(e.target.value)}
            />
          </Col>
        </Row>
      </Modal>
    </PageLoader>
  )
}

const MsrpWhitelistRoutes = props => (
  <RouterSwitch>
    <PrivateRoute exact path={props.match.path} component={MsrpWhitelistList} />
  </RouterSwitch>
)

MsrpWhitelistRoutes.propTypes = {
  match: PropTypes.object
}

export default withRouter(MsrpWhitelistRoutes)
